import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { Eye, FileText, Send, X } from 'lucide-react';
import { Buton, Etiket, Girdi, Iskelet, Kart, KartIcerik, Rozet, toast } from '@emanetx/ui';
import { eposta, type EpostaSablonDetay, type EpostaSablonOzeti } from '@emanetx/sdk';
import { istemci } from '../istemci.js';

export function SayfaSablonlar() {
  const [duzenleId, setDuzenleId] = useState<string | null>(null);

  const liste = useQuery({
    queryKey: ['eposta', 'sablonlar'],
    queryFn: () => eposta.sablonlariListele(istemci),
  });

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">E-posta Şablonları</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          TR/EN şablonlar. Müşterinin dilTercihi'ne göre otomatik seçilir.
        </p>
      </header>

      {liste.isLoading ? (
        <Iskelet className="h-40" />
      ) : (
        <div className="grid gap-3">
          {liste.data?.map((s) => (
            <SablonSatir key={s.id} s={s} onDuzenle={setDuzenleId} />
          ))}
        </div>
      )}

      {duzenleId && <DuzenleDialog id={duzenleId} onKapat={() => setDuzenleId(null)} />}
    </div>
  );
}

function SablonSatir({ s, onDuzenle }: { s: EpostaSablonOzeti; onDuzenle: (id: string) => void }) {
  return (
    <Kart>
      <KartIcerik>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-marka-600" />
              <span className="font-semibold">{s.ad}</span>
              <code className="text-fume-500 font-mono text-xs">{s.kod}</code>
              {s.aktif ? <Rozet ton="yesil">Aktif</Rozet> : <Rozet ton="gri">Pasif</Rozet>}
            </div>
            {s.aciklama && <p className="text-fume-500 mt-1 text-xs">{s.aciklama}</p>}
          </div>
          <Buton stil="cizgili" boyut="kucuk" onClick={() => onDuzenle(s.id)}>
            Düzenle
          </Buton>
        </div>
      </KartIcerik>
    </Kart>
  );
}

interface DuzenleDurum {
  konuTr: string;
  govdeTr: string;
  konuEn: string;
  govdeEn: string;
  aktif: boolean;
}

function DuzenleDialog({ id, onKapat }: { id: string; onKapat: () => void }) {
  const qc = useQueryClient();
  const [sekme, setSekme] = useState<'tr' | 'en'>('tr');
  const [onizlemeAcik, setOnizlemeAcik] = useState(false);
  const [testAlici, setTestAlici] = useState('');
  const [form, setForm] = useState<DuzenleDurum | null>(null);

  useQuery({
    queryKey: ['eposta', 'sablon', id],
    queryFn: async () => {
      const detay = await eposta.sablonDetay(istemci, id);
      setForm({
        konuTr: detay.konuTr,
        govdeTr: detay.govdeTr,
        konuEn: detay.konuEn,
        govdeEn: detay.govdeEn,
        aktif: detay.aktif,
      });
      return detay;
    },
  });

  const sablonSorgu = useQuery<EpostaSablonDetay>({
    queryKey: ['eposta', 'sablon', id, 'meta'],
    queryFn: () => eposta.sablonDetay(istemci, id),
  });

  const kaydet = useMutation({
    mutationFn: () => eposta.sablonGuncelle(istemci, id, form!),
    onSuccess: () => {
      toast.success('Şablon güncellendi');
      void qc.invalidateQueries({ queryKey: ['eposta', 'sablonlar'] });
      onKapat();
    },
    onError: (err: unknown) => toast.error(hataMesaji(err)),
  });

  const test = useMutation({
    mutationFn: () => {
      // Form'daki guncel konu/govde'yi gonder — kullanici kaydetmeden test ederse
      // ekranda gordugu icerik gonderilir (Frontend-Kritik-1).
      const konu = form ? (sekme === 'en' ? form.konuEn : form.konuTr) : '';
      const govde = form ? (sekme === 'en' ? form.govdeEn : form.govdeTr) : '';
      return eposta.sablonTestGonder(istemci, id, {
        alici: testAlici,
        dil: sekme,
        konu,
        govde,
      });
    },
    onSuccess: (sonuc) =>
      sonuc.basarili
        ? toast.success(`Test maili gönderildi (${sekme.toUpperCase()}) — ekrandaki içerikle`)
        : toast.error(`Gönderilemedi: ${sonuc.hata}`),
    onError: (err: unknown) => toast.error(hataMesaji(err)),
  });

  const konu = form ? (sekme === 'en' ? form.konuEn : form.konuTr) : '';
  const govde = form ? (sekme === 'en' ? form.govdeEn : form.govdeTr) : '';

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onKapat()}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 max-h-[92vh] w-[95vw] max-w-3xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <Dialog.Title className="text-lg font-semibold">
                Şablon: {sablonSorgu.data?.ad ?? '...'}
              </Dialog.Title>
              {sablonSorgu.data && (
                <code className="text-fume-500 font-mono text-xs">{sablonSorgu.data.kod}</code>
              )}
            </div>
            <Dialog.Close asChild>
              <button className="text-fume-400 hover:text-fume-700">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          {form ? (
            <div className="space-y-4">
              {/* Dil sekmesi */}
              <div className="bg-fume-100 dark:bg-fume-800 inline-flex rounded-lg p-1">
                {(['tr', 'en'] as const).map((d) => (
                  <button
                    key={d}
                    onClick={() => setSekme(d)}
                    className={`rounded-md px-4 py-1.5 text-sm font-semibold ${
                      sekme === d ? 'dark:bg-fume-700 bg-white shadow' : 'text-fume-500'
                    }`}
                  >
                    {d === 'tr' ? '🇹🇷 Türkçe' : '🇬🇧 English'}
                  </button>
                ))}
              </div>

              <div>
                <Etiket>Konu</Etiket>
                <Girdi
                  value={konu}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      [sekme === 'en' ? 'konuEn' : 'konuTr']: e.target.value,
                    })
                  }
                  placeholder={sekme === 'tr' ? 'Hoş geldin {{ad}}!' : 'Welcome {{ad}}!'}
                />
              </div>

              <div>
                <Etiket>
                  Gövde (HTML) — değişkenler için <code>&#123;&#123;ad&#125;&#125;</code>{' '}
                  sözdizimini kullan
                </Etiket>
                <textarea
                  value={govde}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      [sekme === 'en' ? 'govdeEn' : 'govdeTr']: e.target.value,
                    })
                  }
                  rows={12}
                  className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 mt-1 w-full rounded-lg border bg-white px-3 py-2 font-mono text-xs"
                />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.aktif}
                  onChange={(e) => setForm({ ...form, aktif: e.target.checked })}
                  className="h-4 w-4"
                />
                <span>Şablon aktif</span>
              </label>

              {/* Onizleme */}
              {onizlemeAcik && (
                <div className="border-fume-200 dark:border-fume-700 overflow-hidden rounded-lg border">
                  <div className="bg-fume-50 dark:bg-fume-800 text-fume-700 dark:text-fume-200 flex items-center justify-between border-b px-3 py-2 text-xs font-semibold">
                    <span>Önizleme · {sekme.toUpperCase()}</span>
                    <span className="text-fume-400 text-[10px] font-normal">
                      mail görüntüsü — her zaman aydınlık
                    </span>
                  </div>
                  <iframe
                    title="onizleme"
                    srcDoc={onizlemeIcin(govde)}
                    className="h-72 w-full bg-white"
                    style={{ colorScheme: 'light' }}
                  />
                </div>
              )}

              {/* Aksiyon satırı */}
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <Buton stil="cizgili" boyut="orta" onClick={() => setOnizlemeAcik((o) => !o)}>
                  <Eye size={14} /> {onizlemeAcik ? 'Önizlemeyi Gizle' : 'Önizleme'}
                </Buton>

                <div className="ml-auto flex items-center gap-2">
                  <Girdi
                    type="email"
                    value={testAlici}
                    onChange={(e) => setTestAlici(e.target.value)}
                    placeholder="test@ornek.com"
                    className="w-48"
                  />
                  <Buton
                    stil="cizgili"
                    boyut="orta"
                    onClick={() => test.mutate()}
                    disabled={!testAlici.includes('@') || test.isPending}
                  >
                    <Send size={14} /> {test.isPending ? '...' : `Test (${sekme.toUpperCase()})`}
                  </Buton>
                  <Buton onClick={() => kaydet.mutate()} disabled={kaydet.isPending}>
                    {kaydet.isPending ? 'Kaydediliyor...' : 'Kaydet'}
                  </Buton>
                </div>
              </div>
            </div>
          ) : (
            <Iskelet className="h-80" />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function hataMesaji(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: string }).message);
  }
  return 'Bir hata oluştu';
}

// Onizleme icin tum `{{x}}` placeholder'larini doldurur. Bilinen degiskenler icin
// gercekci ornek, bilinmeyen icin `«x»` ile yer tutucu yazar — sablon yazari neyin
// ne oldugunu gorur.
const ORNEK_DEGISKENLER: Record<string, string> = {
  ad: 'Ahmet Test',
  kod: 'KIR-TEST',
  nokta: 'SAW · Giden Yolcu',
  sure: '4 saat',
  tutar: '40',
  sonKullanim: '31.12.2026',
  email: 'ornek@emanetx.com',
  telefon: '+90 5XX *** ** 67',
  sirket: 'EmanetX',
  tarih: new Date().toLocaleDateString('tr-TR'),
};

function onizlemeIcin(govde: string): string {
  return govde.replace(/\{\{(\w+)\}\}/g, (_eslesme, anahtar: string) => {
    if (ORNEK_DEGISKENLER[anahtar] !== undefined) return ORNEK_DEGISKENLER[anahtar];
    // Tanimsiz degisken — admin'e gorunur yer tutucu
    return `«${anahtar}»`;
  });
}
